import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { exposeService, Expose } from '../services/exposeService';
import { eventService, Event } from '../services/eventService';
import { audioService, Audio } from '../services/audioService';
import { galerieService, GalerieImage } from '../services/galerieService';
import { publicQuestionsService } from '../services/publicQuestionsService';
import api from '../config/api';

export const Home: React.FC = () => {
  const [heroSlide, setHeroSlide] = useState(0);
  const [gallerySlide, setGallerySlide] = useState(0);
  const [aidesSlide, setAidesSlide] = useState(0);
  const [jeudiMatinExpose, setJeudiMatinExpose] = useState<Expose | null>(null);
  const [jeudiSoirExpose, setJeudiSoirExpose] = useState<Expose | null>(null);
  const [mercrediSoirExpose, setMercrediSoirExpose] = useState<Expose | null>(null);
  const [nextEvent, setNextEvent] = useState<Event | null>(null);
  const [latestExposes, setLatestExposes] = useState<Expose[]>([]);
  const [latestAudios, setLatestAudios] = useState<Audio[]>([]);
  const [galerieImages, setGalerieImages] = useState<GalerieImage[]>([]);
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement }>({});
  const audioProgress = useRef<{ [key: number]: { currentTime: number; duration: number } }>({});
  const [, forceUpdate] = useState(0);
  
  // État pour la modale Q/R
  const [showQRModal, setShowQRModal] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);

  // Calculer les slides dynamiquement avec les 3 sections (VCN, HDR, SANTÉ)
  const heroSlides = React.useMemo(() => {
    const slides: Array<{
      expose?: Expose | null;
      event?: Event | null;
      type: string;
      icon: string;
      section?: string;
    }> = [];
    
    // Ajouter un exposé de chaque section
    if (jeudiMatinExpose) slides.push({ expose: jeudiMatinExpose, type: 'VCN', icon: '', section: 'VCN' });
    if (jeudiSoirExpose) slides.push({ expose: jeudiSoirExpose, type: 'HDR', icon: '', section: 'HDR' });
    if (mercrediSoirExpose) slides.push({ expose: mercrediSoirExpose, type: 'SANTÉ', icon: '', section: 'SANTE' });
    // Ajouter l'événement à la fin si disponible
    if (nextEvent) slides.push({ event: nextEvent, type: 'Événement', icon: '', section: nextEvent.section });
    
    return slides;
  }, [jeudiMatinExpose, jeudiSoirExpose, mercrediSoirExpose, nextEvent]);



  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (heroSlides.length === 0) {
      setHeroSlide(0);
      return;
    }
    // S'assurer que heroSlide ne dépasse pas la longueur
    if (heroSlide >= heroSlides.length) {
      setHeroSlide(0);
    }
    const interval = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // 5 secondes pour un rythme plus dynamique
    return () => clearInterval(interval);
  }, [heroSlides.length, heroSlide]);

  useEffect(() => {
    if (galerieImages.length === 0) return;
    const interval = setInterval(() => {
      setGallerySlide((prev) => (prev + 1) % Math.min(galerieImages.length, 3));
    }, 5000);
    return () => clearInterval(interval);
  }, [galerieImages.length]);

  // Défilement automatique des aides sociales
  useEffect(() => {
    const interval = setInterval(() => {
      setAidesSlide((prev) => (prev + 1) % 3);
    }, 4000); // Change toutes les 4 secondes
    return () => clearInterval(interval);
  }, []);

  // Données des aides sociales
  const aidesSociales = [
    {
      icon: '🏥',
      title: 'Prise en charge médicale',
      description: 'La dahira assurera 50% des frais médicaux de ses membres.',
      color: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100'
    },
    {
      icon: '🎓',
      title: 'Aide aux bacheliers',
      description: 'Les nouveaux bacheliers non boursiers bénéficieront des dons de tickets.',
      color: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      iconBg: 'bg-purple-100'
    },
    {
      icon: '💚',
      title: 'Soutien financier',
      description: 'Hanafiya est prêt à soutenir ses membres en cas de besoin.',
      color: 'from-green-50 to-green-100',
      borderColor: 'border-green-200',
      iconBg: 'bg-green-100'
    }
  ];



  const fetchData = async () => {
    try {
      // Récupérer les exposés et événements par section (VCN, HDR, SANTÉ)
      const [vcnExposes, hdrExposes, santeExposes, vcnEvents, hdrEvents, santeEvents, audios, galerie] = await Promise.all([
        exposeService.getAll({ section: 'VCN', ordering: '-date_expose,-heure_debut' }).catch(() => ({ results: [] })),
        exposeService.getAll({ section: 'HDR', ordering: '-date_expose,-heure_debut' }).catch(() => ({ results: [] })),
        exposeService.getAll({ section: 'SANTE', ordering: '-date_expose,-heure_debut' }).catch(() => ({ results: [] })),
        eventService.getAll({ section: 'VCN', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
        eventService.getAll({ section: 'HDR', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
        eventService.getAll({ section: 'SANTE', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
        audioService.getAll().catch(() => ({ results: [] })),
        galerieService.getAll().catch(() => ({ results: [] })),
      ]);

      // Filtrer pour ne garder que ceux à venir ou en cours (pas les terminés) et trier par date
      const vcnExposesList = (vcnExposes.results || [])
        .filter((e: Expose) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Expose, b: Expose) => {
          const dateA = new Date(a.date_expose + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_expose + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      const hdrExposesList = (hdrExposes.results || [])
        .filter((e: Expose) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Expose, b: Expose) => {
          const dateA = new Date(a.date_expose + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_expose + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      const santeExposesList = (santeExposes.results || [])
        .filter((e: Expose) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Expose, b: Expose) => {
          const dateA = new Date(a.date_expose + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_expose + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      
      // Prendre le premier de chaque section pour le slide
      setJeudiMatinExpose(vcnExposesList[0] || null);
      setJeudiSoirExpose(hdrExposesList[0] || null);
      setMercrediSoirExpose(santeExposesList[0] || null);
      
      // Pour les événements, prendre le premier de chaque section et trier par date
      const vcnEventsList = (vcnEvents.results || [])
        .filter((e: Event) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Event, b: Event) => {
          const dateA = new Date(a.date_event + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_event + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      const hdrEventsList = (hdrEvents.results || [])
        .filter((e: Event) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Event, b: Event) => {
          const dateA = new Date(a.date_event + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_event + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      const santeEventsList = (santeEvents.results || [])
        .filter((e: Event) => 
          e.status_auto === 'a_venir' || e.status_auto === 'en_cours'
        )
        .sort((a: Event, b: Event) => {
          const dateA = new Date(a.date_event + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_event + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });
      
      // Trier par date et prendre le premier événement global
      const allUpcomingEvents = [...vcnEventsList, ...hdrEventsList, ...santeEventsList].sort((a: Event, b: Event) => {
        const dateA = new Date(a.date_event + 'T' + (a.heure_debut || '00:00:00'));
        const dateB = new Date(b.date_event + 'T' + (b.heure_debut || '00:00:00'));
        return dateA.getTime() - dateB.getTime();
      });
      setNextEvent(allUpcomingEvents.length > 0 ? allUpcomingEvents[0] : null);
      
      // Pour les 3 prochains exposés, prendre un de chaque section
      const allExposesList = [...vcnExposesList, ...hdrExposesList, ...santeExposesList];
      
      // Trier les exposés à venir par date et prendre les 3 prochains (un de chaque section si possible)
      const upcomingExposesSorted = allExposesList
        .sort((a: Expose, b: Expose) => {
          const dateA = new Date(a.date_expose + 'T' + a.heure_debut);
          const dateB = new Date(b.date_expose + 'T' + b.heure_debut);
          return dateA.getTime() - dateB.getTime();
        });
      
      // Prendre un de chaque section si possible, sinon les 3 prochains
      const selectedExposes: Expose[] = [];
      if (vcnExposesList.length > 0) selectedExposes.push(vcnExposesList[0]);
      if (hdrExposesList.length > 0) selectedExposes.push(hdrExposesList[0]);
      if (santeExposesList.length > 0) selectedExposes.push(santeExposesList[0]);
      
      // Si on n'a pas 3 exposés (une de chaque section), compléter avec les prochains
      if (selectedExposes.length < 3) {
        const remaining = upcomingExposesSorted.filter(e => !selectedExposes.find(se => se.id === e.id));
        selectedExposes.push(...remaining.slice(0, 3 - selectedExposes.length));
      }
      
      setLatestExposes(selectedExposes.slice(0, 3));
      setLatestAudios((audios.results || []).slice(0, 3));
      // Prendre seulement les 3 dernières images de la galerie
      const galerieList = galerie.results || [];
      setGalerieImages(galerieList.slice(0, 3));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = (audioId: number) => {
    const audio = audioRefs.current[audioId];
    if (!audio) return;

    if (playingAudio === audioId) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Pause all other audios
      Object.values(audioRefs.current).forEach((a) => {
        if (a) {
          try {
            a.pause();
          } catch (e) {}
        }
      });
      audio.play().catch((error) => {
        console.error('Erreur de lecture audio:', error);
        setPlayingAudio(null);
      });
      setPlayingAudio(audioId);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (audioId: number, e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRefs.current[audioId];
    if (!audio || !audioProgress.current[audioId]) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * audioProgress.current[audioId].duration;
    audio.currentTime = newTime;
    audioProgress.current[audioId].currentTime = newTime;
  };

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!questionText.trim()) {
      setQuestionError('Veuillez saisir votre question');
      return;
    }
    
    if (questionText.trim().length < 10) {
      setQuestionError('La question doit contenir au moins 10 caractères');
      return;
    }

    setSubmittingQuestion(true);
    setQuestionError(null);
    setQuestionSuccess(false);

    try {
      await publicQuestionsService.createQuestion(questionText.trim());
      setQuestionSuccess(true);
      setQuestionText('');
      setTimeout(() => {
        setShowQRModal(false);
        setQuestionSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      setQuestionError(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section avec Slide */}
      <section className="relative bg-gradient-to-br from-white via-green-50 to-white text-gray-900 overflow-hidden min-h-[400px] sm:min-h-[500px] md:min-h-[600px] lg:min-h-[700px] flex items-center pt-12 sm:pt-16 md:pt-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #2E7D32 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12 md:py-16 w-full">
          {/* Barre de liens WhatsApp des filières */}
          <div className="mb-4 sm:mb-6 animate-fade-in">
            <div className="w-full flex flex-col items-center gap-2 sm:gap-3">
              <p className="text-sm sm:text-base font-semibold text-green-800 text-center leading-snug">
                Rejoins ton groupe WhatsApp de filière (nouveaux / nouvelles bacheliers).
              </p>
              <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-1 sm:pb-0 justify-center w-full">
                <a
                  href="https://chat.whatsapp.com/IiW32soPoPCAG4gycDoOdb?mode=hqrc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-green-600 text-white text-xs sm:text-sm font-semibold shadow-md hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0"
                >
                  <span className="mr-2 text-base sm:text-lg">🟢</span>
                  LEA UFR SES
                </a>
                <a
                  href="https://chat.whatsapp.com/IGhgUNzTfuq3IpqcCXBUy0?mode=hqrc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-green-600 text-white text-xs sm:text-sm font-semibold shadow-md hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0"
                >
                  <span className="mr-2 text-base sm:text-lg">🟢</span>
                  LPC UFR SET
                </a>
                <a
                  href="https://chat.whatsapp.com/Lga5j4V6KJHHCvOjhQlKyv?mode=hqrc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-green-600 text-white text-xs sm:text-sm font-semibold shadow-md hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0"
                >
                  <span className="mr-2 text-base sm:text-lg">🟢</span>
                  LMIO UFR SES
                </a>
                <a
                  href="https://chat.whatsapp.com/BqnBzGENM0p7zAdfEDsF33?mode=hqrc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-green-600 text-white text-xs sm:text-sm font-semibold shadow-md hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0"
                >
                  <span className="mr-2 text-base sm:text-lg">🟢</span>
                  LMI UFR SET
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center">
            {/* Texte à gauche - Hidden sur mobile */}
            <div className="hidden lg:block text-left animate-slide-in-left">
              {/* Bouton Q/R */}
              <div className="mb-6 flex justify-start">
                <button
                  onClick={() => setShowQRModal(true)}
                  className="group relative px-6 py-3 bg-gradient-to-r from-green-600 via-green-500 to-green-600 hover:from-green-700 hover:via-green-600 hover:to-green-700 text-white rounded-full font-bold text-lg shadow-2xl transform transition-all duration-300 hover:scale-110 hover:shadow-green-500/50 animate-pulse hover:animate-none"
                >
                  <span className="flex items-center space-x-2">
                    <span className="text-2xl">❓</span>
                    <span>Poser une Question</span>
                  </span>
                  <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
              </div>
              
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 leading-tight text-gray-900">
                Bienvenue sur<br />
                <span className="bg-gradient-to-r from-green-600 via-green-700 to-green-800 bg-clip-text text-transparent">
                  Hanafia
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-700 mb-4 font-medium">
                Dahira Université de Thiès
              </p>
              
              {/* Section Aides Sociales - Desktop (Slider) */}
              <div className="mb-10 relative">
                <h2 className="text-2xl font-bold text-green-800 mb-4 flex items-center space-x-2">
                  <span>🤲</span>
                  <span>Aides Sociales de la Dahira</span>
                </h2>
                <div className="relative h-[180px] rounded-2xl overflow-hidden shadow-lg border-2 border-green-200/50">
                  {aidesSociales.map((aide, index) => {
                    const isActive = index === aidesSlide;
                    return (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-all duration-700 ${
                          isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0'
                        }`}
                      >
                        <div className={`h-full bg-gradient-to-br ${aide.color} p-6 flex items-center space-x-4`}>
                          <div className={`text-5xl flex-shrink-0 ${aide.iconBg} rounded-2xl p-4 shadow-md`}>
                            {aide.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-2 text-xl">{aide.title}</h3>
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {aide.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Dots Navigation */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                    {aidesSociales.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setAidesSlide(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === aidesSlide ? 'bg-green-700 w-8' : 'bg-white bg-opacity-70 w-2 hover:bg-opacity-90'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Découvrez nos exposés hebdomadaires, événements, bibliothèque complète et médiathèque audio
              </p> */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Link
                  to="/exposes"
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Voir les Exposés
                </Link>
                <Link
                  to="/mediatheque"
                  className="bg-white border-2 border-green-600 text-green-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  Médiathèque
                </Link>
              </div>
            </div>

            {/* Slide à droite - Desktop */}
            <div className="hidden lg:block relative h-[650px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              {heroSlides.length > 0 ? (
              <div className="relative h-full w-full">
                {heroSlides.map((slide, index) => {
                  const isActive = index === heroSlide;
                  const expose = slide.expose;
                  const event = slide.event;

                  return (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-1000 ${
                        isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0'
                      }`}
                    >
                      <div className="relative h-full w-full">
                        {expose?.photo_exposant ? (
                          <img
                            src={expose.photo_exposant}
                            alt={expose.prenom_exposant}
                            className="w-full h-full object-cover"
                          />
                        ) : event?.photo ? (
                          <img
                            src={event.photo}
                            alt={event.titre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-8">
                          {expose && (
                            <>
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="text-yellow-300 font-bold text-lg">{slide.type}</span>
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm text-white">
                                  {expose.section === 'VCN' ? 'VCN' : expose.section === 'HDR' ? 'HDR' : 'SANTÉ'}
                                </span>
                              </div>
                              <h3 className="text-3xl font-bold mb-2 text-white">{expose.theme}</h3>
                              <p className="text-green-100 text-lg mb-1">
                                {expose.prenom_exposant} {expose.nom_exposant} - {expose.classe}
                              </p>
                              <p className="text-green-200 mb-4">
                                {new Date(expose.date_expose).toLocaleDateString('fr-FR', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })} à {expose.heure_debut}
                              </p>
                              <Link
                                to={`/exposes/${expose.url_slug || expose.id}`}
                                className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                              >
                                Voir les détails →
                              </Link>
                            </>
                          )}
                          {event && (
                            <>
                              <h3 className="text-3xl font-bold mb-2 text-white">{event.titre}</h3>
                              <p className="text-green-100 text-lg mb-2 line-clamp-2">{event.description}</p>
                              <p className="text-green-200 mb-4">
                                {new Date(event.date_event).toLocaleDateString('fr-FR', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </p>
                              <Link
                                to={`/events/${event.url_slug || event.id}`}
                                className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                              >
                                Voir les détails →
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Dots Navigation */}
                {heroSlides.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setHeroSlide(index)}
                      className={`h-3 rounded-full transition-all duration-300 ${
                        index === heroSlide ? 'bg-yellow-300 w-10' : 'bg-white bg-opacity-50 w-3 hover:bg-opacity-75'
                      }`}
                    />
                  ))}
                </div>
                )}
              </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                  <div className="text-center text-white">
                    <p className="text-2xl font-bold">Aucun événement à venir</p>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile: Bouton Q/R et Aides Sociales */}
            <div className="lg:hidden space-y-6 mb-6">
              {/* Bouton Poser une Question - En haut */}
              <div className="flex justify-center">
                <button
                  onClick={() => setShowQRModal(true)}
                  className="group relative px-6 py-3 bg-gradient-to-r from-green-600 via-green-500 to-green-600 hover:from-green-700 hover:via-green-600 hover:to-green-700 text-white rounded-full font-bold text-base shadow-2xl transform transition-all duration-300 hover:scale-110 hover:shadow-green-500/50 animate-pulse hover:animate-none w-full max-w-sm"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span className="text-2xl">❓</span>
                    <span>Poser une Question</span>
                  </span>
                  <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
              </div>
              
              {/* Section Aides Sociales - Mobile (Slider) */}
              <div className="relative">
                <h2 className="text-xl font-bold text-green-800 mb-3 flex items-center space-x-2">
                  <span>🤲</span>
                  <span>Aides Sociales du Dahira</span>
                </h2>
                <div className="relative h-[160px] rounded-2xl overflow-hidden shadow-lg border-2 border-green-200/50">
                  {aidesSociales.map((aide, index) => {
                    const isActive = index === aidesSlide;
                    return (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-all duration-700 ${
                          isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0'
                        }`}
                      >
                        <div className={`h-full bg-gradient-to-br ${aide.color} p-4 flex items-center space-x-3`}>
                          <div className={`text-4xl flex-shrink-0 ${aide.iconBg} rounded-xl p-3 shadow-md`}>
                            {aide.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-1 text-base">{aide.title}</h3>
                            <p className="text-gray-700 text-xs leading-relaxed">
                              {aide.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Dots Navigation */}
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                    {aidesSociales.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setAidesSlide(index)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          index === aidesSlide ? 'bg-green-700 w-6' : 'bg-white bg-opacity-70 w-1.5 hover:bg-opacity-90'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile: Only Slide avec infos */}
            <div className="lg:hidden relative h-[500px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              {heroSlides.length > 0 && heroSlides[heroSlide] ? (() => {
                const currentSlide = heroSlides[heroSlide];
                const expose = currentSlide?.expose;
                const event = currentSlide?.event;
                
                return (
                  <div className="relative h-full">
                    {expose?.photo_exposant ? (
                      <img
                        src={expose.photo_exposant}
                        alt="Exposant"
                        className="w-full h-full object-cover"
                      />
                    ) : event?.photo ? (
                      <img
                        src={event.photo}
                        alt="Événement"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                      </div>
                    )}
                    {/* Infos sur mobile */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-6">
                      {expose && (
                        <>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-yellow-300 font-bold text-sm">{currentSlide.type}</span>
                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm text-white">
                              {expose.section === 'VCN' ? 'VCN' : expose.section === 'HDR' ? 'HDR' : 'SANTÉ'}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold mb-1 text-white">{expose.theme}</h3>
                          <p className="text-green-100 text-sm mb-1">
                            {expose.prenom_exposant} {expose.nom_exposant}
                          </p>
                          <p className="text-green-200 text-xs">
                            {new Date(expose.date_expose).toLocaleDateString('fr-FR')} à {expose.heure_debut}
                          </p>
                        </>
                      )}
                      {event && (
                        <>
                          <h3 className="text-xl font-bold mb-1 text-white">{event.titre}</h3>
                          <p className="text-green-100 text-xs">{event.description}</p>
                        </>
                      )}
                    </div>
                    {/* Dots mobile */}
                    {heroSlides.length > 1 && (
                    <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {heroSlides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setHeroSlide(index)}
                          className={`h-2 rounded-full transition-all ${
                            index === heroSlide ? 'bg-yellow-300 w-6' : 'bg-white bg-opacity-50 w-2'
                          }`}
                        />
                      ))}
                    </div>
                    )}
                  </div>
                );
              })() : (
                <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                  <div className="text-center text-white">
                        <p className="text-xl font-bold">Aucun événement à venir</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Galerie Slide avec Texte */}
      {galerieImages.length > 0 && (
        <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
          {/* Motifs décoratifs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-green-100/30 rounded-full blur-2xl"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-stretch">
              {/* Slide Gallery */}
              <div className="relative h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <div className="relative h-full">
                  {galerieImages.slice(0, 3).map((image, index) => (
                    <div
                      key={image.id}
                      className={`absolute inset-0 transition-all duration-1000 ${
                        gallerySlide === index ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0'
                      }`}
                    >
                      <img
                        src={image.image}
                        alt={image.titre}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                        <h4 className="text-white font-bold text-lg mb-1">{image.titre}</h4>
                        {image.description && (
                          <p className="text-gray-200 text-sm line-clamp-2">{image.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Dots - seulement si on a plus d'une image */}
                  {galerieImages.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
                      {galerieImages.slice(0, 3).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setGallerySlide(index)}
                          className={`h-3 rounded-full transition-all duration-300 ${
                            index === gallerySlide ? 'bg-white w-10' : 'bg-white bg-opacity-50 w-3 hover:bg-opacity-75'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Texte informatif */}
              <div className="animate-slide-in-right relative flex items-center">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 sm:p-8 shadow-xl border border-green-100/50 min-h-[500px] sm:min-h-[600px] flex flex-col justify-center">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-6">
                    Notre Galerie
                  </h2>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                    Découvrez les moments forts de nos activités, exposés et événements 
                    à travers notre galerie photo. Chaque image raconte une histoire de 
                    partage, d'apprentissage et de communauté.
                  </p>
                  <ul className="space-y-4 sm:space-y-5 mb-6 sm:mb-10">
                    {[
                      'Moments des exposés hebdomadaires',
                      '🕌 Cérémonies et événements religieux',
                      '👥 Rassemblements communautaires',
                      '🎓 Activités estudiantines',
                    ].map((item, index) => (
                      <li key={index} className="flex items-center space-x-3 sm:space-x-4 text-gray-700 text-sm sm:text-base md:text-lg bg-green-50/50 rounded-lg p-2 sm:p-3 transition-all hover:bg-green-100/50 hover:translate-x-2">
                        <span className="break-words">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/galerie"
                    className="inline-flex items-center justify-center space-x-2 sm:space-x-3 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto mt-auto"
                  >
                    <span className="whitespace-nowrap">Voir la galerie complète</span>
                    <span className="text-lg sm:text-xl">→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section Cours de la Semaine - Phrase qui défile */}
      <section className="relative bg-gradient-to-br from-white via-green-50/40 to-green-100/60 py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-green-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-green-200/25 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 sm:mb-12 relative">
            <div className="inline-block bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-5 shadow-xl border border-green-100/50">
              <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-4">
                Nos Cours Hebdomadaires
              </h2>
              <p className="text-gray-600 text-xl">
                Cours présentiel & en ligne • Samedi 17h30–18h30 • Mercredi 21h • Devant la mosquée
              </p>
            </div>
          </div>

          {/* Phrase qui défile horizontalement */}
          <div 
            className="relative overflow-hidden bg-gradient-to-r from-green-600 via-green-600 to-green-700 rounded-2xl py-8 shadow-2xl border-4 border-white/50"
            onMouseEnter={() => {
              const element = document.getElementById('cours-text-scroll');
              if (element) element.style.animationPlayState = 'paused';
            }}
            onMouseLeave={() => {
              const element = document.getElementById('cours-text-scroll');
              if (element) element.style.animationPlayState = 'running';
            }}
            >
              <div 
              id="cours-text-scroll" 
              className="whitespace-nowrap text-white text-2xl sm:text-3xl font-semibold"
              style={{
                animation: 'scroll-text 40s linear infinite',
                display: 'inline-block'
              }}
            >
              Al Qur-ân • Al Fiqh Mâlikî (Jurisprudence) • Târiqa Tijâniyya — Samedi 17h30–18h30 — Mercredi 21h — Devant la mosquée • 
              <span className="mx-8 text-green-200">━━━</span>
              Al Qur-ân • Al Fiqh Mâlikî (Jurisprudence) • Târiqa Tijâniyya — Samedi 17h30–18h30 — Mercredi 21h — Devant la mosquée • 
              <span className="mx-8 text-green-200">━━━</span>
              Al Qur-ân • Al Fiqh Mâlikî (Jurisprudence) • Târiqa Tijâniyya — Samedi 17h30–18h30 — Mercredi 21h — Devant la mosquée • 
              <span className="mx-8 text-green-200">━━━</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Derniers Exposants et 3 Derniers Audios avec Lecteur */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 left-1/4 w-96 h-96 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-32 right-1/4 w-96 h-96 bg-green-300/15 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-green-100/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 relative">
            <div className="inline-block bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-4 shadow-lg border border-green-100/50">
              <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-4">
                Contenus à Venir
              </h2>
              <p className="text-gray-600 text-xl">
                Découvrez nos prochains exposés et audios
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            {/* 3 Prochains Exposés */}
            <div className="animate-slide-in-left">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                  <span className="text-4xl">⏰</span>
                  <span>Prochains Exposés</span>
                </h3>
                <Link
                  to="/exposes"
                  className="text-green-700 hover:text-green-800 font-semibold text-sm flex items-center space-x-1"
                >
                  <span>Voir tout</span>
                  <span>→</span>
                </Link>
              </div>
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-700 border-t-transparent"></div>
                </div>
              ) : latestExposes.length > 0 ? (
                <div className="space-y-6">
                  {latestExposes.map((expose, index) => (
                    <Link
                      key={expose.id}
                      to={`/exposes/${expose.url_slug || expose.id}`}
                      className="block bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 group transform hover:-translate-y-2 h-[200px] flex items-center"
                    >
                      <div className="flex items-center space-x-5 w-full">
                        {expose.photo_exposant ? (
                          <img
                            src={expose.photo_exposant}
                            alt={expose.prenom_exposant}
                            className="w-24 h-24 rounded-xl object-cover flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center flex-shrink-0 shadow-md">
                            <span className="text-4xl">📝</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-xl text-gray-900 mb-2 group-hover:text-green-700 transition-colors line-clamp-1">
                            {expose.theme}
                          </h4>
                          <p className="text-gray-600 mb-3 font-medium text-sm">
                            {expose.prenom_exposant} {expose.nom_exposant} - {expose.classe}
                          </p>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                              {new Date(expose.date_expose).toLocaleDateString('fr-FR')}
                            </span>
                            <div className="flex space-x-4 text-gray-400">
                              <span>👁️ {expose.nombre_vues || 0}</span>
                              <span>❤️ {expose.nombre_likes || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl shadow-lg">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-600 text-lg">Aucun exposé disponible</p>
                </div>
              )}
            </div>

            {/* 3 Derniers Audios avec Lecteur */}
            <div className="animate-slide-in-right">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                  <span className="text-4xl">🎵</span>
                  <span>Derniers Audios</span>
                </h3>
                <Link
                  to="/mediatheque"
                  className="text-green-700 hover:text-green-800 font-semibold text-sm flex items-center space-x-1"
                >
                  <span>Voir tout</span>
                  <span>→</span>
                </Link>
              </div>
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-700 border-t-transparent"></div>
                </div>
              ) : latestAudios.length > 0 ? (
                <div className="space-y-6">
                  {latestAudios.map((audio, index) => (
                    <div
                      key={audio.id}
                      className="block bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 transform hover:-translate-y-2 h-[200px] flex flex-col justify-center"
                    >
                      <div className="flex items-center space-x-5 mb-4">
                        <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-md relative">
                          <span className="text-4xl">🎵</span>
                          {/* Bouton play centré sur l'icône */}
                          <button
                            onClick={() => toggleAudio(audio.id)}
                            className={`absolute inset-0 rounded-xl flex items-center justify-center transition-all duration-300 ${
                              playingAudio === audio.id
                                ? 'bg-red-500 bg-opacity-80 hover:bg-opacity-90'
                                : 'bg-black bg-opacity-0 hover:bg-opacity-30'
                            }`}
                          >
                            <span className="text-white text-2xl">
                              {playingAudio === audio.id ? '⏸️' : '▶️'}
                            </span>
                          </button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-xl text-gray-900 mb-2 line-clamp-1">
                            {audio.titre}
                          </h4>
                          <p className="text-gray-600 mb-3 font-medium text-sm">
                            {audio.personne} - {audio.categorie}
                          </p>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{audio.duree_formatee || '--:--'}</span>
                            <div className="flex space-x-4 text-gray-400">
                              <span>👁️ {audio.nombre_ecoutes || 0}</span>
                              <span>❤️ {audio.nombre_likes || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Lecteur Audio compact */}
                      <div className="bg-green-50 rounded-xl p-3 mt-2">
                        <audio
                          ref={(el) => {
                            if (el) {
                              audioRefs.current[audio.id] = el;
                              // Ajouter les événements pour suivre la progression
                              el.addEventListener('timeupdate', () => {
                                if (!audioProgress.current[audio.id]) {
                                  audioProgress.current[audio.id] = { currentTime: 0, duration: 0 };
                                }
                                audioProgress.current[audio.id].currentTime = el.currentTime;
                                // Force re-render
                                forceUpdate((prev) => prev + 1);
                              });
                              el.addEventListener('loadedmetadata', () => {
                                if (!audioProgress.current[audio.id]) {
                                  audioProgress.current[audio.id] = { currentTime: 0, duration: 0 };
                                }
                                audioProgress.current[audio.id].duration = el.duration;
                              });
                            }
                          }}
                          src={audio.audio_file}
                          className="hidden"
                          onEnded={() => {
                            setPlayingAudio(null);
                            if (audioProgress.current[audio.id]) {
                              audioProgress.current[audio.id].currentTime = 0;
                            }
                          }}
                        />
                        <div className="flex items-center space-x-2">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>
                                {audioProgress.current[audio.id] 
                                  ? formatTime(audioProgress.current[audio.id].currentTime)
                                  : '0:00'}
                              </span>
                              <span>
                                {audioProgress.current[audio.id] 
                                  ? formatTime(audioProgress.current[audio.id].duration)
                                  : audio.duree_formatee || '--:--'}
                              </span>
                            </div>
                            <div 
                              className="h-1.5 bg-green-200 rounded-full overflow-hidden cursor-pointer"
                              onClick={(e) => handleProgressClick(audio.id, e)}
                            >
                              <div
                                className={`h-full transition-all duration-300 ${
                                  playingAudio === audio.id ? 'bg-green-600' : 'bg-green-300'
                                }`}
                                style={{ 
                                  width: audioProgress.current[audio.id] && audioProgress.current[audio.id].duration
                                    ? `${(audioProgress.current[audio.id].currentTime / audioProgress.current[audio.id].duration) * 100}%`
                                    : '0%'
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl shadow-lg">
                  <div className="text-6xl mb-4">🎵</div>
                  <p className="text-gray-600 text-lg">Aucun audio disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modale Q/R */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-700 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center space-x-2">
                    <span>❓</span>
                    <span>Poser une Question</span>
                  </h2>
                  <p className="text-green-100 text-sm mt-1">
                    Posez votre question islamique. Un administrateur y répondra bientôt.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowQRModal(false);
                    setQuestionText('');
                    setQuestionError(null);
                    setQuestionSuccess(false);
                  }}
                  className="text-white hover:text-gray-200 text-2xl transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitQuestion} className="p-6 space-y-4">
              {questionSuccess ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-green-700 font-semibold mb-2">Votre question a été envoyée avec succès !</p>
                  <p className="text-green-600 text-sm mb-4">
                    Un administrateur y répondra bientôt. Vous pouvez consulter toutes les questions sur la page Q/R.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <Link
                      to="/faq"
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                      onClick={() => setShowQRModal(false)}
                    >
                      Voir toutes les questions →
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Votre question * <span className="text-gray-500">(minimum 10 caractères)</span>
                    </label>
                    <textarea
                      value={questionText}
                      onChange={(e) => {
                        setQuestionText(e.target.value);
                        setQuestionError(null);
                      }}
                      rows={6}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                        questionError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Posez votre question islamique ici..."
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1">{questionText.length} caractères</p>
                    {questionError && (
                      <p className="mt-1 text-sm text-red-600">{questionError}</p>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm flex items-start space-x-2">
                      <span className="text-lg">💡</span>
                      <span>
                        <strong>Astuce :</strong> Vous pouvez consulter toutes les questions et réponses sur la page{' '}
                        <Link to="/faq" className="underline font-semibold hover:text-blue-900">
                          Q/R
                        </Link>
                        . Si vous êtes connecté, vos questions seront sauvegardées dans votre profil.
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowQRModal(false);
                        setQuestionText('');
                        setQuestionError(null);
                      }}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={submittingQuestion || !questionText.trim() || questionText.trim().length < 10}
                      className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {submittingQuestion ? 'Envoi...' : '✅ Envoyer la question'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
